import React, { useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { U2000IdHeaderCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers, type U2000FormStateHandlersReturn } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import { NS, U3139ROLES } from "../../../../U3139";
import { emptyDetailLot, u3139FormatDetailLotCadastreLabel, u3139FormatDistrictLabel, u3139ResolveDetailLotRankLabel, type U3139DetailLot, type U3139MinutesFormRow } from "../../../../U3139Service";
import U3139DetailLotsEditDlg from "./U3139DetailLotsEditDlg";
import U3139DetailLotsDeleteDlg from "./U3139DetailLotsDeleteDlg";
import { u3139DetailLotsHandler } from "./U3139DetailLotsHandlers";

export type U3139MinutesFormExtendedHandlers = U2000FormStateHandlersReturn<U3139MinutesFormRow> & {
  addNewMinute?: () => void;
  modifyMinute?: () => void;
  deleteMinute?: () => void;
};

interface U3139DetailLotsBodyProps {
  minutesFormStateHandlers: U3139MinutesFormExtendedHandlers;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "id",
      label: "",
      widthClass: "w-[50px]",
      type: "number",
      align: "center",
      headerDisplay: (column, label, allRowSelected, handleSelectAllRow) => (
        <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />
      ),
      display: (column, value, row, allowRowSelection, rowUpdated) => (
        <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allowRowSelection} />
      ),
      isCriteriaField: false,
    },
    {
      name: "initialLot",
      label: t("lblInitialLot", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "district.propertyDistrict",
      label: t("lblDistrict", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
      display: (_column, _value, row) => u3139FormatDistrictLabel((row as U3139DetailLot).district),
    },
    {
      name: "cadastre.cadastre",
      label: t("lblCadastre", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
      display: (_column, _value, row) => u3139FormatDetailLotCadastreLabel(row as U3139DetailLot),
    },
    {
      name: "rankLabel",
      label: t("lblLotRank", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
      display: (_column, _value, row) => u3139ResolveDetailLotRankLabel(row as U3139DetailLot),
    },
  ];
}

const U3139DetailLotsBody: React.FC<U3139DetailLotsBodyProps> = ({ minutesFormStateHandlers }) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const [formState, formStateDispatch] = useReducer<U2000FormState, [action: { type: string; data?: unknown }]>(u2000FormState, u2000IntFormState);
  const gridStateHandlers = useU2000GridStateHandlers<U3139DetailLot>();

  const validateDetailLot = (lot: U3139DetailLot) => {
    const result: Record<string, string> = {};
    if (!lot.initialLot?.trim()) result.initialLot = t("errInitialLotRequired", { ns: NS });
    return result;
  };

  const detailLotBL: U2000FormSBusinessHandlers<U3139DetailLot> = {
    newRecord: () => emptyDetailLot(),
    validateForm: validateDetailLot,
  };

  const detailLotFormHandlers = useU2000FormStateHandlers<U3139DetailLot>({
    formState,
    formStateDispatch,
    businessHandlers: detailLotBL,
  });

  const handler = u3139DetailLotsHandler({ gridStateHandlers, t, formStateHandlers: detailLotFormHandlers });

  const detailLots = minutesFormStateHandlers.getData()?.detailLots;

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((c) => c.isDefaultColumn).map((c) => c.name));

    const raw = Array.isArray(detailLots) ? detailLots : [];
    const list = raw.map((l) => ({ ...l, id: l.id?.trim() || generateTemporaryId() }));
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
  }, [t, detailLots]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) return;
    minutesFormStateHandlers.setData({
      ...minutesFormStateHandlers.getData(),
      detailLots: gridStateHandlers.getData(),
    });
  }, [handler.dataChangedEvent]);

  const isReadOnly = minutesFormStateHandlers.getFormState() === U2000FormStateEnumState.READ;

  const disabledUpdateBtn = isReadOnly || gridStateHandlers.getSelectedRows().length !== 1;
  const disableDeleteBtn = isReadOnly || gridStateHandlers.getSelectedRows().length === 0;

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      handleClick: handler.onAdd,
      hasRole: hasRole(U3139ROLES.ADD) || hasRole(U3139ROLES.EDIT),
      disabled: isReadOnly,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      handleClick: handler.onModify,
      hasRole: hasRole(U3139ROLES.EDIT),
      disabled: disabledUpdateBtn,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      handleClick: handler.onDelete,
      hasRole: hasRole(U3139ROLES.EDIT),
      disabled: disableDeleteBtn,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
  ];

  return (
    <>
      <U3139DetailLotsEditDlg handler={handler} t={t} NS={NS} />
      <U3139DetailLotsDeleteDlg handler={handler} t={t} NS={NS} />
      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={!isReadOnly}
        label={t("lblDetailLots", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
};

export default U3139DetailLotsBody;
